import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { takeUntil, Subject, of, switchMap, tap, catchError, timeout, map, forkJoin } from 'rxjs';

import { Punk } from '@/models/graph';
import { PunkGridViewComponent } from '@/components/punk-grid-view/punk-grid-view.component';
import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { UsdPipe } from '@/pipes/usd.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';

import { DataService } from '@/services/data.service';
import { StateService } from '@/services/state.service';
import { Web3Service } from '@/services/web3.service';
import { ThemeService } from '@/services/theme.service';
import { TransactionTrackingService } from '@/services/transaction-tracking.service';
import { DexScreenerService, MarketData } from '@/services/dexscreener.service';
import { SlotCounterComponent } from '@/components/slot-counter/slot-counter.component';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TimestampPipe } from '@/pipes/timestamp.pipe';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { environment } from 'src/environments/environment';
import { CigState, CigStateDto } from '@/models/cig';
import { FormsModule } from '@angular/forms';
import { PieChartComponent, PieSlice } from '@/components/pie-chart/pie-chart.component';
import { parseUnits, stringToHex } from 'viem';
import { TimeagoModule } from 'ngx-timeago';
import { TippyDirective } from '@/directives/tippy.directive';

@Component({
  selector: 'app-cig-claim',
  standalone: true,
  imports: [CommonModule, RouterModule, WeiToEthPipe, UsdPipe, FormatCashPipe, TokenIdParsePipe, TimestampPipe, TimeagoModule, LazyLoadImageModule, FormsModule, WalletAddressDirective, SlotCounterComponent, PieChartComponent, PunkGridViewComponent, TippyDirective],
  templateUrl: './cigclaim.component.html',
  styleUrls: ['./cigclaim.component.scss']
})
export class CigClaimComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isConnected = false;
  walletAddress: string | null = null;
  isCigClaimTransactionActive = false;

  // UI: progress towards next purchase (0-100)
  purchaseProgress: number = 66;

  // Market data from DexScreener
  marketData: MarketData | null = null;

  ownedPunks: Punk[] = [];
  claimableWei: bigint = 0n;
  claimable: number = 0;
  perTokenAmounts: { tokenId: number; amountWei: bigint; amount: number }[] = [];
  loadingClaimable = false;
  claiming = false;

  // Protocol-owned purchases (by CIG protocol/CEO address)
  protocolOwned: { tokenId: string; boughtAt: number; boughtWei: string; listingWei?: string | null }[] = [];
  loadingProtocolOwned = false;

  // Computed properties for token display
  get displayedTokens(): { tokenId: number; amountWei: bigint; amount: number }[] {
    return this.perTokenAmounts.slice(0, 8);
  }

  get hasMoreTokens(): boolean {
    return this.perTokenAmounts.length > 9;
  }

  get remainingTokensCount(): number {
    return Math.max(0, this.perTokenAmounts.length - 8);
  }

  get remainingTokensAmount(): number {
    return this.perTokenAmounts.slice(8).reduce((total, token) => total + token.amount, 0);
  }

  get canClaim(): boolean {
    return !this.claiming && this.claimableWei > 0n;
  }

  cigStatsDto: CigStateDto | null = null;
  feeSplits: any = {
    mainFee: 2,
    cigBurnSplit: 50,
    devSplit: 25,
    ceoSplit: 25
  };

  // Listings offered to CEO
  listingsOfferedToCEO: any[] = [];
  acceptingListing = false;
  private listingsLoading = false;

  // Outer ring: overall tax vs the rest (10% tax)
  overallTaxSlices: PieSlice[] = [
    { label: 'Overall Tax', value: 10, color: 'rgba(255, 207, 115, 1)' },
    { label: 'Other', value: 90, color: 'rgba(255,255,255,0.15)' },
  ];

  // Inner ring: decomposition of the 10% tax
  // Main fee is 2% of the total; remaining 8% is split 50/25/25
  taxBreakdownSlices: PieSlice[] = [
    { label: 'Main Fee', value: 2, color: 'rgba(250, 128, 114, 1)' },
    { label: 'CIG Burn', value: 4, color: 'rgba(178, 139, 255, 1)' },
    { label: 'Developers', value: 2, color: 'rgba(107, 219, 255, 1)' },
    { label: 'CEO', value: 2, color: 'rgba(166, 255, 143, 1)' },
  ];

  get safePieSlices(): PieSlice[] {
    return this.overallTaxSlices || [];
  }

  // Legend helpers
  readonly overallTaxPercent: number = 10;
  get taxRemainderPercent(): number {
    return Math.max(0, this.overallTaxPercent - (Number(this.feeSplits?.mainFee) || 0));
  }
  get mainAbsPercent(): number {
    return Number(this.feeSplits?.mainFee) || 0;
  }
  get mainRelPercent(): number {
    const overall = this.overallTaxPercent || 0;
    return overall > 0 ? (this.mainAbsPercent / overall) * 100 : 0;
  }
  get burnAbsPercent(): number {
    return this.taxRemainderPercent * ((Number(this.feeSplits?.cigBurnSplit) || 0) / 100);
  }
  get devAbsPercent(): number {
    return this.taxRemainderPercent * ((Number(this.feeSplits?.devSplit) || 0) / 100);
  }
  get ceoAbsPercent(): number {
    return this.taxRemainderPercent * ((Number(this.feeSplits?.ceoSplit) || 0) / 100);
  }

  // Takeover modal state
  takeoverOpen = false;
  buying = false;
  form = {
    priceCig: 10_000_001,
    punkId: 1,
    graffiti: '',
    days: 7,
  };

  // Transaction tracking

  constructor(
    protected dataSvc: DataService,
    public stateSvc: StateService,
    private web3Svc: Web3Service,
    public themeSvc: ThemeService,
    public txTrackingSvc: TransactionTrackingService,
    private dexscreenerSvc: DexScreenerService,
  ) {
    console.log('CigClaimComponent constructor called with ALL services including DexScreenerService');
  }
  protected readonly environment = environment;

  ngOnInit(): void {
    console.log('CigClaimComponent ngOnInit called');
    
    // Test with minimal subscriptions
    this.stateSvc.web3Connected$.pipe(takeUntil(this.destroy$)).subscribe((connected) => {
      this.isConnected = connected;
    });
    
    this.stateSvc.walletAddress$.pipe(takeUntil(this.destroy$)).subscribe((addr) => {
      this.walletAddress = addr;
      if (addr) {
        this.loadOwned(addr);
        this.loadCigStats(addr);
      } else {
        this.ownedPunks = [];
        this.claimableWei = 0n;
        this.claimable = 0;
      }
    });

    // Load listings offered to the CIG protocol (CEO address) - independent of current wallet
    this.loadListingsOfferedToCEO();

    // Load CIG stats for the protocol address
    this.loadCigStats('0x000000000000000000000000000000000074eda0');

    // Load protocol-owned NFTs (purchased by protocol)
    this.loadProtocolOwned();

    // Load market data from DexScreener
    this.dexscreenerSvc.marketData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.marketData = data;
    });
  }

  private async loadCigStats(address: string): Promise<void> {
    try {
      this.cigStatsDto = await this.web3Svc.getCigStatsDto(address);
      // Properly divide bigint by 1e18n and convert to number for display
      // Divide the bigint by 1e18n before converting to number for accuracy
      this.form.priceCig = this.cigStatsDto?.takeOverPrice !== null && this.cigStatsDto?.takeOverPrice !== undefined
        ? Number(this.cigStatsDto.takeOverPrice / 1_000_000_000_000_000_000n)
        : 0;
      
      // Listings are loaded independently of the current wallet
    } catch (e) {
      console.error('loadCigStats error', e);
      this.cigStatsDto = null;
    }
  }

  private loadProtocolOwned(): void {

    this.loadingProtocolOwned = true;
    // this.dataSvc.fetchBoughtEvents(ceoAddress).pipe(
    this.dataSvc.fetchBoughtEvents('0x696ed7b26f4b019ceec78dc8b9140ad64a6f354b').pipe(
      takeUntil(this.destroy$),
      map((events: any[]) => {
        // Normalize to minimal structure and sort desc by date
        const MIN_WEI = 1_000_000_000_000_000n; // 0.001 ETH
        const normalized = (events || [])
          // filter out small-value purchases (< 0.001 ETH)
          .filter(ev => {
            const val = typeof ev.value === 'string' ? ev.value : String(ev.value ?? '0');
            try { return BigInt(val) > MIN_WEI; } catch { return false; }
          })
          .map(ev => ({
          tokenId: String(ev.tokenId),
          boughtAt: Number(ev.blockTimestamp) || 0,
          boughtWei: ev.value,
        })).sort((a, b) => b.boughtAt - a.boughtAt);
        return normalized;
      }),
      switchMap((items) => {
        if (!items?.length) return of([]);
        // Fetch current listings for the first 24 tokens to avoid excessive calls
        const slice = items.slice(0, 24);
        const listingCalls = slice.map(it => this.dataSvc.fetchSinglePunkListing(it.tokenId).pipe(
          map(listing => ({ tokenId: it.tokenId, listingWei: listing?.value || null }))
        ));
        return forkJoin(listingCalls).pipe(
          map(listings => {
            const MIN_WEI = 1_000_000_000_000_000n; // 0.001 ETH
            const listingMap = new Map(listings.map(l => [l.tokenId, l.listingWei]));
            const merged = items.map(it => ({
              ...it,
              listingWei: listingMap.get(it.tokenId) ?? null
            }));
            // Final guard: drop any item whose boughtWei <= 0.001 ETH
            return merged.filter(m => {
              try { return BigInt(typeof m.boughtWei === 'string' ? m.boughtWei : String(m.boughtWei ?? '0')) > MIN_WEI; }
              catch { return false; }
            });
          })
        );
      }),
      catchError((err) => {
        console.error('loadProtocolOwned error', err);
        return of([]);
      }),
      tap(() => this.loadingProtocolOwned = false)
    ).subscribe((items: any[]) => {
      // Extra safety: filter out items that might still be <= 0.001 ETH / invalid
      const MIN_WEI = 1_000_000_000_000_000n; // 0.001 ETH
      this.protocolOwned = (items as any[]).filter(it => {
        try { return BigInt(typeof it.boughtWei === 'string' ? it.boughtWei : String(it.boughtWei ?? '0')) > MIN_WEI; }
        catch { return false; }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connect(): void {
    this.web3Svc.connect();
  }

  private loadOwned(address: string): void {
    this.dataSvc.fetchOwned(address).pipe(
      takeUntil(this.destroy$),
      tap((res) => {
        this.ownedPunks = res || [];
        // Trigger preview after owned punks are loaded
        this.refreshClaimable();
      })
    ).subscribe();
  }

  // ===== TAKEOVER =====
  taxPerDayCig: number = 0;

  totalCostCig: number = 0;

  openTakeover(): void {
    this.takeoverOpen = true;
    this.calculateTakeoverCosts();
  }

  calculateTakeoverCosts(): void {
    const price = Number(this.form.priceCig || 0);
    this.taxPerDayCig = price / 1000;
    this.totalCostCig = price + (this.taxPerDayCig * Number(this.form.days || 0)) + 0.007;
  }

  closeTakeover(): void {
    this.takeoverOpen = false;
  }

  private toBytes32(text: string): `0x${string}` {
    return stringToHex(text ?? '', { size: 32 }) as `0x${string}`;
  }


  async confirmTakeover(): Promise<void> {
    try {
      this.buying = true;
      this.isCigClaimTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the takeover transaction in your wallet.</p>');
      
      // Build arguments for buyCEO(_max_spend,_new_price,_tax_amount,_punk_index,_graffiti)
      const priceWei = parseUnits(String(this.form.priceCig || 0), 18);
      const taxAmountWei = parseUnits(String(this.taxPerDayCig * (this.form.days || 0)), 18);
      const maxSpendWei = priceWei + taxAmountWei; // cap
      const punkIndex = BigInt(this.form.punkId || 0);
      const graffitiBytes = this.toBytes32(this.form.graffiti?.slice(0, 32));

      const txHash = await this.web3Svc.cigContractInteraction('buyCEO', [
        maxSpendWei,
        priceWei,
        taxAmountWei,
        punkIndex,
        graffitiBytes,
      ]);
      
      if (txHash) {
        this.txTrackingSvc.setTransactionMessage(txHash, '<p>Your takeover transaction is being processed on the Ethereum network.</p>');
        const receipt = await this.web3Svc.waitForTransaction(txHash);
        this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Your takeover transaction is complete! You are now the CEO.</p>');
        
        if (this.walletAddress) {
          await this.loadCigStats(this.walletAddress);
        }
        setTimeout(() => {
          this.isCigClaimTransactionActive = false;
        }, 3000);
      }
    } catch (e) {
      console.error('confirmTakeover error', e);
      this.txTrackingSvc.setErrorTransactionMessage(e, '<p>There was an error with your takeover transaction.</p>');
      setTimeout(() => {
        this.isCigClaimTransactionActive = false;
      }, 3000);
    } finally {
      this.buying = false;
      this.closeTakeover();
    }
  }

  refreshClaimable(): void {
    if (!this.walletAddress) return;
    const ids = (this.ownedPunks || []).map(p => Number(p.id));
    this.loadingClaimable = true;
    this.web3Svc.cigAirdropPreviewClaim(this.walletAddress, ids)
      .then((res) => {
        if (!res) {
          this.perTokenAmounts = [];
          this.claimableWei = 0n;
          this.claimable = 0;
          return;
        }

        // res is bigint[][] ordered by collection; take the non-empty one
        const [phunksAmts, punksV2Amts, punksV1Amts] = res as unknown as bigint[][];
        let amounts: bigint[] = [];
        if (phunksAmts?.length) amounts = phunksAmts;
        else if (punksV2Amts?.length) amounts = punksV2Amts;
        else if (punksV1Amts?.length) amounts = punksV1Amts;

        this.perTokenAmounts = ids.map((id, i) => ({
          tokenId: id,
          amountWei: amounts?.[i] ?? 0n,
          amount: Number((amounts?.[i] ?? 0n)) / 1e18
        }));

        this.claimableWei = this.perTokenAmounts.reduce((acc, x) => acc + x.amountWei, 0n);
        this.claimable = Number(this.claimableWei) / 1e18;
      })
      .finally(() => this.loadingClaimable = false);
  }

  async claim(): Promise<void> {
    if (!this.walletAddress || this.claimableWei <= 0n) return;
    try {
      this.claiming = true;
      this.isCigClaimTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the claim transaction in your wallet.</p>');
      
      const ids = (this.ownedPunks || []).map(p => Number(p.id));
      const txHash = await this.web3Svc.cigAirdropClaim(ids);
      
      if (txHash) {
        this.txTrackingSvc.setTransactionMessage(txHash, '<p>Your claim transaction is being processed on the Ethereum network.</p>');
        const receipt = await this.web3Svc.waitForTransaction(txHash);
        this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Your claim transaction is complete! Your $CIG tokens have been claimed.</p>');
        this.refreshClaimable();
        setTimeout(() => {
          this.isCigClaimTransactionActive = false;
        }, 3000);
      }
    } catch (e) {
      console.error(e);
      this.txTrackingSvc.setErrorTransactionMessage(e, '<p>There was an error with your claim transaction.</p>');
      setTimeout(() => {
        this.isCigClaimTransactionActive = false;
      }, 3000);
    } finally {
      this.claiming = false;
    }
  }

  // Helper methods for market data display
  get cigPrice(): string {
    return this.marketData ? this.dexscreenerSvc.formatPrice(this.marketData.priceUsd) : '—';
  }

  get cigPriceChange(): number {
    return this.marketData?.priceChange24h || 0;
  }

  get cigPriceChangeClass(): string {
    return this.dexscreenerSvc.getPriceChangeClass(this.cigPriceChange);
  }

  get marketCap(): string {
    return this.marketData ? this.dexscreenerSvc.formatMarketCap(this.marketData.marketCap) : '—';
  }

  get volume24h(): string {
    return this.marketData ? this.dexscreenerSvc.formatVolume(this.marketData.volume24h) : '—';
  }

  get ethPrice(): string {
    return this.marketData?.ethPriceUsd ? this.dexscreenerSvc.formatEthPrice(this.marketData.ethPriceUsd) : '—';
  }

  // New methods for market data actions
  async buyNftForProtocol(): Promise<void> {
    // TODO: Implement NFT buying for protocol
    console.log('Buy NFT for Protocol functionality to be implemented');
  }

  async burnCig(): Promise<void> {
    // TODO: Implement CIG burning functionality
    console.log('Burn CIG functionality to be implemented');
  }

  // Load listings offered to CIG protocol (CEO address from configuration)
  private loadListingsOfferedToCEO(): void {
    // Prevent multiple simultaneous calls
    if (this.listingsLoading) {
      console.log('Listings already loading, skipping...');
      return;
    }
    
    this.listingsLoading = true;
    
    // Use the CEO address from environment configuration
    const ceoAddress = environment.cigCEOAddress;
    console.log('Loading listings offered to CIG protocol (CEO address):', ceoAddress);
    
    // Add timeout and better error handling to prevent browser freeze
    this.dataSvc.fetchListingsOfferedTo(ceoAddress).pipe(
      takeUntil(this.destroy$),
      timeout(15000), // 15 seconds timeout to prevent infinite waiting
      tap((listings: any[]) => {
        console.log('Listings offered to CIG protocol:', listings);
        this.listingsOfferedToCEO = listings || [];
        this.listingsLoading = false;
      }),
      catchError((error) => {
        console.error('Error loading listings for CIG protocol:', error);
        this.listingsOfferedToCEO = [];
        this.listingsLoading = false;
        // Don't propagate the error to prevent component crashes
        return of([]);
      })
    ).subscribe({
      next: (listings: any[]) => {
        console.log('Successfully loaded listings:', listings?.length || 0);
      },
      error: (error) => {
        console.error('Fatal error in listings subscription:', error);
        this.listingsLoading = false;
      }
    });
  }

  // Accept a listing and buy the NFT for the protocol
  async acceptListing(listing: any): Promise<void> {
    if (!this.walletAddress || !listing) return;
    
    try {
      this.acceptingListing = true;
      this.isCigClaimTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the listing acceptance transaction in your wallet.</p>');
      
      // TODO: Implement the actual contract interaction to accept the listing
      // This would typically involve calling a contract method to accept the listing
      // and transfer the NFT to the protocol address
      
      console.log('Accepting listing:', listing);
      
      // For now, just simulate the transaction
      // In a real implementation, you would call the appropriate contract method
      // const txHash = await this.web3Svc.acceptListing(listing.id);
      
      // Simulate transaction for demo purposes
      const txHash = '0x' + Math.random().toString(16).substr(2, 64);
      
      if (txHash) {
        this.txTrackingSvc.setTransactionMessage(txHash, '<p>Your listing acceptance transaction is being processed on the Ethereum network.</p>');
        
        // Simulate waiting for transaction
        setTimeout(() => {
          this.txTrackingSvc.setProcessTransactionMessage({ status: 'success' }, '<p>Your listing acceptance transaction is complete! The NFT has been purchased for the protocol.</p>');
          
          // Refresh the listings after successful acceptance
          this.loadListingsOfferedToCEO();
          setTimeout(() => {
            this.isCigClaimTransactionActive = false;
          }, 3000);
        }, 3000);
      }
    } catch (e) {
      console.error('acceptListing error', e);
      this.txTrackingSvc.setErrorTransactionMessage(e, '<p>There was an error with your listing acceptance transaction.</p>');
      setTimeout(() => {
        this.isCigClaimTransactionActive = false;
      }, 3000);
    } finally {
      this.acceptingListing = false;
    }
  }
}


